import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../services/unifiedAnalytics';
import { waitForServices } from '../services/servicesInitialized';

/**
 * PageViewTracker Component
 *
 * Automatically tracks page views when the route changes.
 * Waits for analytics services to initialize before tracking.
 * This component should be placed inside the Router but doesn't render anything.
 */
const PageViewTracker = () => {
  const location = useLocation();
  const [servicesReady, setServicesReady] = useState(false);

  // Wait for services to initialize
  useEffect(() => {
    waitForServices().then(() => {
      setServicesReady(true);
    });
  }, []);

  // Track page views once services are ready
  useEffect(() => {
    if (!servicesReady) return;

    // Get the page path
    const pagePath = location.pathname;

    // Generate a user-friendly page title based on the route
    const pageTitle = getPageTitle(pagePath);

    // Track the page view
    trackPageView(pagePath, pageTitle);
  }, [location, servicesReady]);

  // This component doesn't render anything
  return null;
};

/**
 * Generate a user-friendly page title from the route path
 */
const getPageTitle = (path: string): string => {
  const titles: Record<string, string> = {
    '/': 'Home',
    '/login': 'Login',
    '/signup': 'Sign Up',
    '/offers': 'Subscription Offers',
    '/billing': 'Billing',
    '/subscription': 'Subscription Details',
    '/auth/action': 'Email Action',
  };

  return titles[path] || path.replace(/^\//, '').replace(/-/g, ' ');
};

export default PageViewTracker;
